"use client"

import { Area, AreaChart, CartesianGrid, XAxis, ResponsiveContainer } from "recharts"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import React from "react"

const ActivityChart = ({ departmentOptions, departmentColors, chartData, visibleDepartments }: {
  departmentOptions: { value: string, label: string }[],
  departmentColors: string[],
  chartData: { month: string, [key: string]: number | string }[],
  visibleDepartments: string[],
}) => {
  const chartConfig: ChartConfig = departmentOptions.reduce((acc, option, idx) => {
    acc[option.value] = {
      label: option.label,
      color: departmentColors[idx % departmentColors.length],
    }
    return acc
  }, {} as ChartConfig)

  return (
    <div className="w-full border p-4 rounded-md mt-2 overflow-x-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xl">Activity</span>
        {/* <span className="flex items-center gap-1">
          <span className="text-green-700 bg-green-100 rounded px-2 py-0.5 text-xs font-semibold">+23%</span>
          <span className="text-xs text-muted-foreground">vs Last Month</span>
        </span> */}
      </div>
      <div className="flex gap-4 mb-4">
        {departmentOptions.map((option, idx) => (
          visibleDepartments.includes(option.value) && (
            <span key={option.value} className="flex items-center gap-1 text-sm">
              <span
                className="inline-block w-1 h-4 rounded"
                style={{ backgroundColor: departmentColors[idx % departmentColors.length] }}
              />
              {option.label}
            </span>
          )
        ))}
      </div>
      {/* No data available check */}
      {(!chartData.length ||
        chartData.every(row => visibleDepartments.every(dep => !row[dep] || row[dep] === 0))) ? (
        <div className="flex items-center justify-center h-[250px] w-full text-gray-400">
          No data available
        </div>
      ) : (
        <div style={{ minWidth: Math.max(600, chartData.length * 80) }} className="pr-2">
          <ChartContainer config={chartConfig} className="h-[250px] w-full mt-12">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, left: 24, right: 24, bottom: 10 }}
              >
                <defs>
                  {departmentOptions.map((option, idx) => (
                    visibleDepartments.includes(option.value) && (
                      <linearGradient
                        key={option.value}
                        id={`colorArea-${option.value}`}
                        x1="0" y1="0" x2="0" y2="1"
                      >
                        <stop offset="5%" stopColor={departmentColors[idx % departmentColors.length]} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={departmentColors[idx % departmentColors.length]} stopOpacity={0.05} />
                      </linearGradient>
                    )
                  ))}
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  interval={0}
                  axisLine={false}
                  tickFormatter={(value) => String(value)}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" />}
                />
                {departmentOptions.map((option, idx) => (
                  visibleDepartments.includes(option.value) && (
                    <Area
                      key={option.value}
                      dataKey={option.value}
                      type="monotone"
                      fill={`url(#colorArea-${option.value})`}
                      stroke={departmentColors[idx % departmentColors.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 5 }}
                    />
                  )
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      )}
    </div>
  )
}

export default ActivityChart
